import {
	Stack, TextField, Button, CircularProgress, Accordion, AccordionDetails, AccordionSummary,
	Dialog, DialogTitle, DialogContent, DialogActions,
	Select, MenuItem, InputLabel, FormControl, Tooltip, IconButton
} from "@mui/material";
import Icon from "components/icon/icon";
import { addLocationsets, getLocationsetsItems } from "api/layer";
import { AppAction, AppContext } from "helpers/context";
import { LocationSourceOption } from "helpers/models";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./create-modal.module.scss";
import { RightPanel } from "helpers/constants";
import { getLanguage } from "helpers/translations";
import "dayjs/locale/en"; // 导入中英文语言包
import "dayjs/locale/zh"; // 导入中英文语言包

function CreateModal() {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [isError, setIsError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [expanded, setExpanded] = useState(true);
	const [formData, setFormData] = useState({
		name: "",
		locationSource: "latlng",
		aggregationUnit: "numberOfLocations",
		comment: "",
			currency: context.language === "en" ? "CNY" : "人民币"
	});
	const [aggregationUnitOptions, setAggregationUnitOptions] = useState([{
		value: "numberOfLocations",
		name: "Number of Locations",
		disabled: false
	}, {
		value: "total",
		name: "total",
		disabled: true
	}, {
		value: "numberOfRisks",
		name: "Number of Risks",
		disabled: true
	}, {
		value: "building",
		name: "Building",
		disabled: true
	}, {
		value: "content",
		name: "Content",
		disabled: true
	}, {
		value: "businessInterruption",
		name: "Business Interruption",
		disabled: true
	}, {
		value: "propertyDamage",
		name: "Property Damage",
		disabled: true
	}, {
		value: "limit",
		name: "Limit",
		disabled: true
	}, {
		value: "deductible",
		name: "Deductible",
		disabled: true
	}, {
		value: "other",
		name: "Other",
		disabled: true
	}]);

	const handleChangeExpand = () => {
		setExpanded(!expanded);
	};

	const close = (): void => {
		dispatch({ type: AppAction.setCreateModal, payload: false });
	};

	const handleChange = (event: any) => {
		const { name, value } = event.target;
		if (name === "name") {
			if (value) {
				setIsError(false);
			} else {
				setIsError(true);
			}
		}

		setFormData((prevData) => ({ ...prevData, [name]: value }));
	};

	const loadData = (): void => {
		setLoading(true);
		let params = {
			...formData
		};

		addLocationsets(params)
			.then(async (result: any) => {
				if (result.code && result.code === 400) {
					dispatch({
						type: AppAction.addSnackBarParams,
						payload: {
							title: "tip",
							titleIconColor: "red",
							titleIcon: "error",
							resMessage: getLanguage() === "en" ? result.data.messageEN : result.data.messageCN,
						},
					});

					dispatch({ type: AppAction.setSingleLocationAnalysed, payload: false });
					dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
					setLoading(false);
					return;
				}
				if (!result || !result.data || !result.data.id) {
					dispatch({
						type: AppAction.addSnackBarParams,
						payload: {
							titleIconColor: "yellow",
							titleIcon: "warning",
							title: "errorTitle",
							message: "errorMessage",
						},
					});
					setLoading(false);
					close();
					return
				}
				let res = await getLocationsetsItems(result.data.id);
				if (context.locationSets) {
					dispatch({ type: AppAction.setLocationSets, payload: [...context.locationSets, res.data] });
				} else {
					dispatch({ type: AppAction.setLocationSets, payload: [res.data] });
				}
				setLoading(false);
				dispatch({ type: AppAction.setSearchLocationInfo, payload: undefined });
				dispatch({ type: AppAction.setSingleLocationAnalysed, payload: undefined });
				dispatch({ type: AppAction.setCurrentMultipleLocations, payload: res.data });
				dispatch({ type: AppAction.setMultipleLocationsAnalysed, payload: false });
				dispatch({ type: AppAction.setRightPanel, payload: RightPanel.Multiple });
				dispatch({
					type: AppAction.addSnackBarParams,
					payload: {
						title: "createdLocationSetTitle",
						messageData: params.name,
						message: "createdLocationSetMessage",
					},
				});
				close();
			})
			.catch((error) => {
				console.error(error);
				dispatch({
					type: AppAction.addSnackBarParams,
					payload: {
						titleIconColor: "yellow",
						titleIcon: "warning",
						title: "errorTitle",
						message: "errorMessage",
					},
				});
				setLoading(false);
				close();
			});
	};

	return (
		<Dialog
			open={!!context.createModal}
			onClose={close}
		>
			<DialogTitle
				sx={{
					fontWeight: 600,
					fontSize: "16px",
					opacity: 0.79,
					color: "#333",
					width: "500px",
				}}
			>
				{t("locations.createModal.title")}
			</DialogTitle>
			<DialogContent
				sx={{
					paddingTop: "10px!important",
					overflowY: "auto",
					"&::-webkit-scrollbar": { width: "8px" },
					"&::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: "4px" },
					"&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#555" },
					"&::-webkit-scrollbar-track": { background: "#f1f1f1" },
				}}
			>
				<Stack
					direction="row"
					spacing={2}
					sx={{
						justifyContent: "flex-start",
						alignItems: "center",
						marginBottom: "35px",
					}}>

					<TextField
						required
						label={t("locations.createModal.text1")}
						value={formData.name}
						onChange={handleChange}
						name="name"
						inputProps={{ maxLength: 180 }}
						onBlur={(e) => {
							if (e.target.value) {
								setIsError(false);
							} else {
								setIsError(true);
							}
						}}
						onFocus={() => {
							setIsError(false);
						}}
						color="success"
						error={isError}
						helperText={isError && t("locations.createModal.helperText")}
						sx={{
							width: 350,
							position: "relative",
							"& .MuiFormHelperText-root": {
								position: "absolute",
								bottom: "-24px"
							}
						}}
					/>
					<Tooltip
						title={t("locations.createModal.tooltip1")}
						placement="top"
					>
						<IconButton style={{ marginLeft: "10px" }}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<span className="material-icons">info</span>
						</IconButton>
					</Tooltip>
				</Stack>
				<Stack
					direction="row"
					spacing={2}
					sx={{
						justifyContent: "flex-start",
						alignItems: "center",
						marginBottom: "20px",
					}}
				>
					<TextField
						label={t("locations.add.currency")}
						value={formData.currency}
						onChange={handleChange}
						name="currency"
						inputProps={{ maxLength: 20 }}
						color="success"
						sx={{
							width: 350,
							position: "relative",
							"& .MuiFormHelperText-root": {
								position: "absolute",
								bottom: "-24px"
							}
						}}
					/>
				</Stack>
				<p className={styles.title}>{t("locations.createModal.locationDisplayOptions")}</p>
				<FormControl fullWidth style={{ margin: "5px 0 20px" }} disabled>
					<InputLabel color="success" >{t("locations.createModal.locationSource")}</InputLabel>
					<Select
						name="locationSource"
						value={formData.locationSource}
						onChange={handleChange}
						color="success"
						label={t("locations.createModal.locationSource")}
						sx={{
							width: 350,
						}}
						MenuProps={{
							PaperProps: {
								style: {
									maxHeight: 300,
								},
							},
						}}
					>
						<MenuItem value="latlng">{t("locations.createModal.latlng")}</MenuItem>
						<MenuItem value="address">{t("locations.createModal.address")}</MenuItem>
					</Select>
				</FormControl>
				<Stack
					direction="row"
					spacing={2}
					sx={{
						justifyContent: "flex-start",
						alignItems: "center",
					}}>
					<FormControl fullWidth sx={{
						width: 350,
					}}>
						<InputLabel color="success">{t("locations.createModal.aggregationUnit")}</InputLabel>
						<Select
							name="aggregationUnit"
							value={formData.aggregationUnit}
							color="success"
							label={t("locations.createModal.aggregationUnit")}
							MenuProps={{
								PaperProps: {
									style: {
										maxHeight: 300,
									},
								},
							}}
						>
							{aggregationUnitOptions.map((item) => (
								item.disabled ?
									<Tooltip title={t("locations.createModal.tooltip4")} placement="top" disableInteractive key={`tooltip-${item.value}`}>
										<span
											style={{
												display: "block",
												width: "100%",
											}}
										>
											<MenuItem value={item.value} disabled >{t("locations." + item.name)}</MenuItem>
										</span>
									</Tooltip>
									:
									<MenuItem
										value={item.value}
										disabled={item.disabled}
										onClick={() => {
											if (!item.disabled) {
												handleChange({ target: { name: "aggregationUnit", value: item.value } });
											}
										}}
										sx={{
											padding: item.disabled ? "0!important" : "",
										}}
										key={item.value}
									>{t("locations." + item.name)}
									</MenuItem>
							))}
						</Select>
					</FormControl>
					<Tooltip
						title={t("locations.createModal.tooltip2")}
						placement="top"
					>
						<IconButton style={{ marginLeft: "10px" }}
							onClick={(e) => {
								e.stopPropagation();
							}}
						>
							<span className="material-icons">info</span>
						</IconButton>
					</Tooltip>
				</Stack>
				<Accordion
					expanded={expanded}
					onChange={handleChangeExpand}
					sx={{
						boxShadow: "none",
						backgroundColor: "transparent",
						margin: "0!important",
						"&::before": {
							display: "none",
						},
					}}
				>
					<AccordionSummary
						expandIcon={<div className="material-icons">keyboard_arrow_right</div>}
						sx={{
							padding: 0,
							margin: 0,
							flexDirection: "row-reverse",
							"& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
								transform: "rotate(90deg)",
							},
							"& .MuiTypography-root": {
								fontFamily: "Roboto-Regular",
							},
							"&.Mui-expanded": {
								minHeight: "48px",
							},
							"& .MuiAccordionSummary-content.Mui-expanded": {
								margin: "12px 0",
							},
							"& .MuiAccordionSummary-content": {
								alignItems: "center",
							}
						}}
					>
						{t("locations.createModal.additionalInfo")}
						<Tooltip
							title={t("locations.createModal.tooltip3")}
							placement="top"
						>
							<IconButton style={{ marginLeft: "10px" }}
								onClick={(e) => {
									e.stopPropagation();
								}}
							>
								<span className="material-icons">info</span>
							</IconButton>
						</Tooltip>
					</AccordionSummary>
					<AccordionDetails
						sx={{
							padding: "6px 0 0",
							width: "100%",
						}}
					>
						<Stack spacing={{ width: 350, xy: 1, }}>
							<TextField
								multiline
								label={t("locations.createModal.comment")}
								name="comment"
								value={formData.comment}
								onChange={handleChange}
								color="success"
								inputProps={{
									style: {
										resize: "vertical",
									},
									maxLength: 50
								}}
								rows={2}
								sx={{
									marginBottom: "15px",
								}}
							/>
						</Stack>
					</AccordionDetails>
				</Accordion>
			</DialogContent>
			<DialogActions style={{ display: "flex", alignItems: "center", padding: "16px 24px" }}>
				<Button
					color="success"
					onClick={close}
				>
					{t("buttons.cancel")}
				</Button>
				&nbsp;&nbsp;
				<Button
					variant="contained"
					color="success"
					onClick={loadData}
					disabled={!formData.name || loading}
				>
					{loading ? <CircularProgress size={24} color="inherit" /> : t("buttons.create")}
				</Button>
			</DialogActions>
		</Dialog >
	);
}

export default CreateModal;

